/**
 * Shown instead of the app when Supabase credentials are missing.
 *
 * A first `npm run dev` on a fresh clone has no `.env.local`, and a blank page
 * with a console throw is a bad way to learn that. This says exactly what to
 * do instead.
 */
export function SetupRequired() {
  return (
    <div className="mx-auto flex min-h-screen max-w-2xl flex-col justify-center px-5 py-16">
      <p className="text-xs uppercase tracking-[0.32em] text-lamp-400">
        Davis's Reel Thoughts
      </p>

      <h1 className="mt-5 font-display text-5xl leading-tight text-paper-50">
        Almost there — add your Supabase keys
      </h1>

      <p className="mt-4 leading-relaxed text-paper-400">
        The site needs a Supabase project for accounts and image storage. This
        screen replaces the app until two environment variables are set.
      </p>

      <ol className="mt-8 space-y-5">
        <Step number={1} title="Create a Supabase project">
          Go to{" "}
          <a
            href="https://supabase.com/dashboard"
            target="_blank"
            rel="noreferrer noopener"
            className="text-lamp-300 underline underline-offset-4 hover:text-lamp-400"
          >
            supabase.com/dashboard
          </a>{" "}
          and create a free project. Any region is fine.
        </Step>

        <Step number={2} title="Copy the API details">
          In the project, open <Code>Project Settings → API</Code> and copy the{" "}
          <Code>Project URL</Code> and the <Code>anon</Code> public key.
        </Step>

        <Step number={3} title="Create frontend/.env.local">
          <pre className="mt-2 overflow-x-auto rounded-sm border border-ink-600 bg-ink-850 p-3 text-xs leading-relaxed text-paper-200">
            {`VITE_SUPABASE_URL=https://YOUR-PROJECT.supabase.co
VITE_SUPABASE_ANON_KEY=your-anon-key`}
          </pre>
        </Step>

        <Step number={4} title="Set up the backend too">
          Copy <Code>backend/.env.example</Code> to <Code>backend/.env</Code>,
          fill in the database URL and service role key, then run{" "}
          <Code>npm run db:migrate</Code> and <Code>npm run dev</Code> in{" "}
          <Code>backend/</Code>.
        </Step>

        <Step number={5} title="Restart the dev server">
          Vite reads environment variables at startup, so stop and restart{" "}
          <Code>npm run dev</Code> after saving the file.
        </Step>
      </ol>

      <p className="mt-10 border-t border-ink-700 pt-6 text-sm text-paper-500">
        Full instructions, including how to make yourself an admin, are in the
        README at the root of the project.
      </p>
    </div>
  );
}

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-4">
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-lamp-400 text-sm font-semibold text-lamp-400"
      >
        {number}
      </span>
      <div>
        <h2 className="font-display text-lg text-paper-50">{title}</h2>
        <div className="mt-1 text-sm leading-relaxed text-paper-400">
          {children}
        </div>
      </div>
    </li>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded-sm bg-ink-700 px-1.5 py-0.5 text-[0.85em] text-paper-200">
      {children}
    </code>
  );
}
